import { ColumnVisibilityItem } from "providers/Auth";
import { type SortingState } from "@tanstack/react-table";

export const LEADS_COLUMNS_TAB_KEY = "leads";

export const LEADS_PIPELINE_COLUMN_KEY = "LEADS_PIPELINE_COLUMN";

export const DEFAULT_PER_PAGE = window.localStorage.getItem("per_page") || "10";

export const SESSION_STORAGE_KEY = "leads_table_query_params";

// Saved-views mode keeps column visibility as ephemeral working state (not
// persisted to the user record). We still mirror it to sessionStorage so the
// full working state — filters (URL) + columns — survives a page reload and the
// active saved view can be recovered instead of falling back to "Custom".
export const SESSION_STORAGE_COLUMNS_KEY = "leads_table_columns_visibility";

export const SESSION_STORAGE_PIPELINE_ORDER_KEY =
  "leads_table_pipeline_stage_order";

export const BASE_URL_PARAM_KEYS = [
  "search",
  "page",
  "per_page",
  "starred"
] as const;

// Only the global /leads page binds these to the URL — on tabbed routes
// (property/unit/contact) they would collide with the applications table,
// which manages the same "properties"/"assignee_ids"/"sort_by"/"sort_dir" keys
const FILTER_URL_PARAM_KEYS = [
  "properties",
  "units",
  "stages",
  "lead_sources",
  "tags",
  "lead_types",
  "activity_types",
  "approval_likelihood_bands",
  "prescreening_results",
  "from_waitlist",
  "needs_follow_up",
  "created_within",
  "created_after",
  "created_before",
  "showing_within",
  "showing_after",
  "showing_before",
  "assignee_ids",
  "sort_by",
  "sort_dir"
] as const;

export const URL_PARAM_KEYS = [
  ...BASE_URL_PARAM_KEYS,
  ...FILTER_URL_PARAM_KEYS,
  "view"
] as const;

export const DEFAULT_URL_PARAMS: Record<string, string> = Object.fromEntries(
  URL_PARAM_KEYS.map((key) => [key, ""])
);

export const BASE_DEFAULT_URL_PARAMS: Record<string, string> =
  Object.fromEntries(BASE_URL_PARAM_KEYS.map((key) => [key, ""]));

export const LEADS_COLUMNS_CONTEXT_LABEL = "Leads";

export enum LeadView {
  List = "list",
  Pipeline = "pipeline"
}

export enum LeadColumnId {
  Name = "name",
  NeedsFollowUp = "needs_follow_up",
  PropertyInterested = "property_interested",
  UnitInterested = "unit_interested",
  Type = "lead_type",
  Source = "source",
  Tags = "tags",
  Score = "approval_likelihood",
  PrescreeningResult = "prescreening_result",
  FraudIndicators = "fraud_indicators",
  LastNote = "last_note",
  RecentActivity = "recent_activity",
  CreatedAt = "created_at",
  AssignedTo = "assigned_to",
  StageSelector = "stage_selector"
}

export const DEFAULT_SORTING: SortingState = [
  { id: LeadColumnId.RecentActivity, desc: true }
];

export enum LeadSortColumnId {
  LastNote = "last_note",
  RecentActivity = "recent_activity",
  Score = "approval_likelihood",
  PrescreeningResult = "prescreening_result",
  CreatedAt = "created_at"
}

export const LEAD_SORT_COLUMN_MAP: Record<LeadSortColumnId, string> = {
  [LeadSortColumnId.LastNote]: "last_note_at",
  [LeadSortColumnId.RecentActivity]: "most_recent_activity",
  [LeadSortColumnId.Score]: "approval_likelihood",
  [LeadSortColumnId.PrescreeningResult]: "prescreening_result",
  [LeadSortColumnId.CreatedAt]: "created_at"
};

export const LEADS_COLUMN_ORDER: LeadColumnId[] = [
  LeadColumnId.Name,
  LeadColumnId.NeedsFollowUp,
  LeadColumnId.PropertyInterested,
  LeadColumnId.UnitInterested,
  LeadColumnId.Type,
  LeadColumnId.Source,
  LeadColumnId.Tags,
  LeadColumnId.Score,
  LeadColumnId.PrescreeningResult,
  LeadColumnId.LastNote,
  LeadColumnId.FraudIndicators,
  LeadColumnId.RecentActivity,
  LeadColumnId.CreatedAt,
  LeadColumnId.AssignedTo,
  LeadColumnId.StageSelector
];

const LEADS_DEFAULT_COLUMN_VISIBILITY: ColumnVisibilityItem[] = [
  {
    id: LeadColumnId.Name,
    label: "Name",
    checked: true,
    disabled: false
  },
  {
    id: LeadColumnId.NeedsFollowUp,
    label: "Needs follow-up",
    checked: true,
    disabled: false
  },
  {
    id: LeadColumnId.PropertyInterested,
    label: "Property interested",
    checked: true,
    disabled: false
  },
  {
    id: LeadColumnId.UnitInterested,
    label: "Unit interested",
    checked: true,
    disabled: false
  },
  {
    id: LeadColumnId.Source,
    label: "Source",
    checked: true,
    disabled: false
  },
  {
    id: LeadColumnId.Tags,
    label: "Tags",
    checked: true,
    disabled: false
  },
  {
    id: LeadColumnId.LastNote,
    label: "Last note",
    checked: true,
    disabled: false
  },
  {
    id: LeadColumnId.FraudIndicators,
    label: "Fraud indicators",
    checked: true,
    disabled: false
  },
  {
    id: LeadColumnId.RecentActivity,
    label: "Most recent activity",
    checked: true,
    disabled: false
  },
  {
    id: LeadColumnId.CreatedAt,
    label: "Created at",
    checked: true,
    disabled: false
  },
  {
    id: LeadColumnId.AssignedTo,
    label: "Assigned to",
    checked: true,
    disabled: false
  },
  {
    id: LeadColumnId.StageSelector,
    label: "Current stage",
    checked: true,
    disabled: true
  }
];

// The Type column only exists for owner-leads partners, so it's added to the
// default visibility (and thus the column-visibility panel + saved views) only
// when the feature is enabled. Inserted before Source to match LEADS_COLUMN_ORDER.
const LEADS_TYPE_COLUMN_VISIBILITY: ColumnVisibilityItem = {
  id: LeadColumnId.Type,
  label: "Type",
  checked: true,
  disabled: false
};

// The Approval likelihood column only exists for partners with the AI
// lead-scoring feature, so it's added to the default visibility (and thus the
// column-visibility panel + saved views) only when the flag is enabled.
// Inserted after Tags to match LEADS_COLUMN_ORDER.
const LEADS_SCORE_COLUMN_VISIBILITY: ColumnVisibilityItem = {
  id: LeadColumnId.Score,
  label: "Approval likelihood",
  checked: true,
  disabled: false
};

// Inserted after Tags, or after Approval likelihood when that column is shown, to
// match LEADS_COLUMN_ORDER.
const LEADS_PRESCREENING_RESULT_COLUMN_VISIBILITY: ColumnVisibilityItem = {
  id: LeadColumnId.PrescreeningResult,
  label: "Prescreening result",
  checked: true,
  disabled: false
};

export const getLeadsDefaultColumnVisibility = (
  isOwnerLeadsEnabled: boolean,
  isLeadScoringEnabled = false
): ColumnVisibilityItem[] => {
  const columns = [...LEADS_DEFAULT_COLUMN_VISIBILITY];

  if (isOwnerLeadsEnabled) {
    const sourceIndex = columns.findIndex(
      (col) => col.id === LeadColumnId.Source
    );
    columns.splice(
      sourceIndex === -1 ? columns.length : sourceIndex,
      0,
      LEADS_TYPE_COLUMN_VISIBILITY
    );
  }

  if (isLeadScoringEnabled) {
    const tagsIndex = columns.findIndex((col) => col.id === LeadColumnId.Tags);
    columns.splice(
      tagsIndex === -1 ? columns.length : tagsIndex + 1,
      0,
      LEADS_SCORE_COLUMN_VISIBILITY
    );
  }

  const prescreeningAnchorId = isLeadScoringEnabled
    ? LeadColumnId.Score
    : LeadColumnId.Tags;
  const prescreeningAnchorIndex = columns.findIndex(
    (col) => col.id === prescreeningAnchorId
  );
  columns.splice(
    prescreeningAnchorIndex === -1
      ? columns.length
      : prescreeningAnchorIndex + 1,
    0,
    LEADS_PRESCREENING_RESULT_COLUMN_VISIBILITY
  );

  return columns;
};
